import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiMagnify } from '@mdi/js';
import { apiService, RfqSubmission } from '../../services/apiService';
import { DataTable, Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { useToast } from '../components/Toast';
import { adminTheme } from '../styles/adminTheme';

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${adminTheme.colors.text};
  margin-bottom: 16px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const TabBar = styled.div`
  display: flex;
  gap: 2px;
  background: #f1f3f4;
  border-radius: 6px;
  padding: 2px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 6px 12px;
  font-size: 12px;
  font-weight: ${p => p.$active ? 600 : 400};
  color: ${p => p.$active ? 'white' : adminTheme.colors.textSecondary};
  background: ${p => p.$active ? adminTheme.colors.primary : 'transparent'};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    ${p => !p.$active && `background: #e8eaed;`}
  }
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 6px;
  background: white;
  flex: 1;
  max-width: 300px;

  input {
    border: none;
    outline: none;
    font-size: 13px;
    flex: 1;
    color: ${adminTheme.colors.text};
  }
`;

const statusTabs = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'quoted', label: 'Quoted' },
  { key: 'won', label: 'Won' },
  { key: 'lost', label: 'Lost' },
];

export const RfqListPage: React.FC = () => {
  const [rfqs, setRfqs] = useState<RfqSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    setLoading(true);
    const params: { status?: string; search?: string } = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    if (search.trim()) params.search = search;
    apiService.getRfqs(params)
      .then(setRfqs)
      .catch(() => toast('Failed to load RFQs', 'error'))
      .finally(() => setLoading(false));
  }, [statusFilter, search, toast]);

  const columns: Column<RfqSubmission>[] = useMemo(() => [
    { key: 'name', header: 'Name', sortable: true },
    { key: 'company', header: 'Company', sortable: true },
    { key: 'email', header: 'Email', sortable: true },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'circuit_info',
      header: 'Circuit',
      render: (row) => {
        const nodes = row.circuit_schema?.nodes?.length ?? 0;
        const edges = row.circuit_schema?.edges?.length ?? 0;
        return <span>{nodes} nodes, {edges} edges</span>;
      },
    },
    {
      key: 'created_at',
      header: 'Date',
      sortable: true,
      render: (row) => new Date(row.created_at).toLocaleDateString(),
    },
  ], []);

  return (
    <div>
      <PageTitle>RFQ Pipeline</PageTitle>

      <Toolbar>
        <TabBar>
          {statusTabs.map(t => (
            <Tab key={t.key} $active={statusFilter === t.key} onClick={() => setStatusFilter(t.key)}>
              {t.label}
            </Tab>
          ))}
        </TabBar>
        <SearchBox>
          <Icon path={mdiMagnify} size={0.65} color={adminTheme.colors.textMuted} />
          <input
            placeholder="Search name, company, email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </SearchBox>
      </Toolbar>

      <DataTable
        columns={columns}
        data={rfqs}
        loading={loading}
        onRowClick={(row) => navigate(`/panel/rfq/${row.id}`)}
        emptyMessage="No RFQs found"
      />
    </div>
  );
};
